import React, {useState, useEffect} from "react";
import {
    Box,
    Button,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Checkbox
} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {PathList} from "../../Common/Routes/Path";
import AxiosInstance from "../../Common/Api/ApiHelper";
import {apiList} from "../../Common/Api/ApiList";
import {toast} from "react-toastify";
import ErrorToaster from "../../Component/ErrorToaster";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";

const ConvertChallengeScreen = () => {
    const navigate = useNavigate();
    const {id} = useParams();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [criteriaOptions, setCriteriaOptions] = useState([]);

    const [challengeFile, setChallengeFile] = useState(null);

    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);

    const [badgesOptions, setBadgesOptions] = useState([]);

    const initial = {
        challenge_name: "",
        description: "",
        criteria_type: "",
        criteria_value: "",
        xp_points: "",
        user_type: "",
        is_active: "",
        category_id: "",
        sub_category_id: "",
        rating_value: "",
        duration_days: "",
        reward_badge_id: ""
    };

    const [challengeData, setChallengeData] = useState(initial);

    const [errors, setErrors] = useState({
        challenge_name: "",
        description: "",
        criteria_type: "",
        xp_points: "",
        is_active: "",
        user_type: "",
        category_id: "",
        sub_category_id: "",
        duration_days: ""
    });

    useEffect(() => {
        const fetchCriteriaTypes = async () => {
            try {
                const response = await AxiosInstance.get(apiList?.CriteriaTypes);
                const criteriaArray = Array.isArray(response.data?.r?.data)
                    ? response.data.r.data
                    : [];

                setCriteriaOptions(criteriaArray);
            } catch (error) {
                console.error("Failed to fetch criteria types", error);
                setCriteriaOptions([]);
            }
        };

        fetchCriteriaTypes();
    }, []);

    useEffect(() => {
        const fetchCategory = async () => {
            try {
                const response = await AxiosInstance.get(apiList?.getCategory);
                const categoryArray = Array.isArray(response.data?.r)
                    ? response.data.r
                    : [];

                setCategories(categoryArray);
            } catch (error) {
                console.error("Failed to fetch CategoryOptions", error);
                setCategories([]);
            }
        };

        fetchCategory();
    }, []);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setChallengeData({...challengeData, [name]: value});
    };

    const handleFileChange = (e) => {
        setChallengeFile(e.target.files[0]);
    };

    const handleCategoryChange = (event) => {
        const selectedCategoryId = event.target.value;

        setChallengeData({
            ...challengeData,
            category_id: selectedCategoryId,
            sub_category_id: "",
        });

        setBadgesOptions([]);

        const selectedCategory = categories.find(
            (cat) => cat.cat_id === selectedCategoryId
        );

        setSubCategories(selectedCategory ? selectedCategory.sub_cat_id : []);
    };

    const handleSubCategoryChange = (event) => {
        setChallengeData({
            ...challengeData,
            sub_category_id: event.target.value
        });
    };

    useEffect(() => {
        const fetchBadge = async () => {
            try {
                const response = await AxiosInstance.get(`${apiList.GetBadgeById}?id=${id}`);
                if (response.data.s === 1) {
                    const data = response.data.r;

                    const formattedCategoryId = data.cat_id ? Number(data.cat_id) : "";
                    const formattedSubCategoryId = data.sub_cat_id ? Number(data.sub_cat_id) : "";

                    setChallengeData({
                        ...data,
                        is_active: String(data.is_active),
                        category_id: formattedCategoryId,
                        sub_category_id: formattedSubCategoryId,
                        reward_badge_id: data.id,
                        challenge_name: data.badge_name,
                    });

                    // ✅ Find selected category and populate subCategories
                    const selectedCategory = categories.find(
                        (cat) => cat.cat_id === formattedCategoryId
                    );

                    if (selectedCategory) {
                        setSubCategories(selectedCategory.sub_cat_id || []);
                    }
                } else {
                    toast.error(response.data.m || "Failed to fetch badge");
                }
            } catch (error) {
                ErrorToaster(error);
            }
        };

        if (id && categories.length > 0) {
            fetchBadge();
        }
    }, [id, categories]);

    useEffect(() => {
        const fetchBadges = async () => {
            try {
                if (!challengeData.category_id || !challengeData.sub_category_id || !challengeData.criteria_type) {
                    setBadgesOptions([]);
                    return;
                }

                const response = await AxiosInstance.get(
                    `${apiList.getBadges}?cat_id=${challengeData.category_id}&sub_cat_id=${challengeData.sub_category_id}&criteria_type=${challengeData.criteria_type}`
                );

                const badgesArray = Array.isArray(response.data?.r?.data)
                    ? response.data.r.data
                    : [];

                setBadgesOptions(badgesArray);
            } catch (error) {
                console.error("Failed to fetch badges", error);
                setBadgesOptions([]);
            }
        };

        fetchBadges();
    }, [challengeData.category_id, challengeData.sub_category_id, challengeData.criteria_type]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        const newErrors = {};

        if (!challengeData.challenge_name) newErrors.challenge_name = "Name is required";
        if (!challengeData.description) newErrors.description = "Description is required";
        if (!challengeData.criteria_type) newErrors.criteria_type = "Criteria Type is required";
        if (!challengeData.xp_points) newErrors.xp_points = "XP Points is required";
        if (!challengeData.is_active) newErrors.is_active = "Status is required";
        if (!challengeData.user_type) newErrors.user_type = "User Type is required";
        if (!challengeData.category_id) newErrors.category_id = "Category is required";
        if (!challengeData.sub_category_id) newErrors.sub_category_id = "Sub Category is required";
        if (!challengeData.duration_days) newErrors.duration_days = "Duration is required";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        } else {
            setErrors({});
        }

        setIsSubmitting(true);
        try {
            const formData = new FormData();

            Object.keys(challengeData).forEach((key) => {
                formData.append(key, challengeData[key]);
            });

            if (challengeFile) {
                formData.append("challenge_icon", challengeFile);
            }

            const response = await AxiosInstance.post(apiList?.AddChallenge, formData, {
                headers: {"Content-Type": "multipart/form-data"},
            });

            if (response.data.s === 1) {
                toast.success("Challenge created successfully");

                setTimeout(() => {
                    navigate(PathList.challenge);
                }, 1000);
            } else {
                toast.error(response.data.m);
            }
        } catch (error) {
            ErrorToaster(error);
        } finally {
            setIsSubmitting(false);
        }
        setChallengeData(initial);
    }

    return (
        <Box sx={{width: "100%"}}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5, // adjust spacing as needed
                    pb: 2,
                }}
            >
                <ArrowBackIosRoundedIcon
                    sx={{
                        cursor: "pointer",
                        "&:hover": {
                            color: "#3C75D4",
                        },
                    }}
                    onClick={() => navigate(PathList.Badge)}
                />
                <Typography variant="h6" sx={{fontSize: "28px"}}>
                    Add Challenge
                </Typography>
            </Box>

            {/* Form Card */}
            <Card sx={{mt: 3, p: 2}}>
                <CardContent>
                    <form style={{width: "100%"}} onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Name</Typography>
                                <TextField
                                    name="challenge_name"
                                    placeholder="Enter challenge name"
                                    fullWidth
                                    value={challengeData.challenge_name}
                                    onChange={handleChange}
                                />
                                {errors.challenge_name && (
                                    <Typography sx={{color: "red", fontSize: "14px"}}>
                                        {errors.challenge_name}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Criteria</Typography>
                                    <Select
                                        name="criteria_type"
                                        value={challengeData.criteria_type}
                                        onChange={handleChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            Select Criteria
                                        </MenuItem>
                                        {criteriaOptions.map((option) => (
                                            <MenuItem key={option.id} value={option.id}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    {errors.criteria_type && (
                                        <Typography sx={{color: "red", fontSize: "14px"}}>
                                            {errors.criteria_type}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            {challengeData.criteria_type !== 6 && (
                                <Grid item xs={12} md={4}>
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Target</Typography>
                                    <TextField
                                        name="criteria_value"
                                        type="number"
                                        placeholder="Enter criteria value"
                                        fullWidth
                                        value={challengeData.criteria_value}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} md={4}>
                                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Duration (Days)</Typography>
                                <TextField
                                    name="duration_days"
                                    type="number"
                                    placeholder="Enter duration"
                                    fullWidth
                                    value={challengeData.duration_days}
                                    onChange={handleChange}
                                />
                            </Grid>

                            {challengeData.criteria_type === 4 && (
                                <Grid item xs={12} md={4}>
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Rating</Typography>
                                    <TextField
                                        name="rating_value"
                                        type="text"
                                        placeholder="Enter rating value"
                                        fullWidth
                                        value={challengeData.rating_value}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>
                                        Category
                                    </Typography>
                                    <Select
                                        name="category_id"
                                        value={challengeData.category_id}
                                        onChange={handleCategoryChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            Select Category
                                        </MenuItem>
                                        {categories.map((cat) => (
                                            <MenuItem key={cat.cat_id} value={cat.cat_id}>
                                                {cat.cat_name}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    {errors.category_id && (
                                        <Typography sx={{color: "red", fontSize: "14px"}}>
                                            {errors.category_id}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>
                                        Sub Category
                                    </Typography>
                                    <Select
                                        name="sub_category_id"
                                        value={challengeData.sub_category_id}
                                        onChange={handleSubCategoryChange}
                                        displayEmpty
                                        disabled={!challengeData.category_id}
                                    >
                                        <MenuItem value="" disabled>
                                            Select Sub Category
                                        </MenuItem>

                                        {subCategories.map((sub) => (
                                            <MenuItem key={sub.sub_cat_id} value={sub.sub_cat_id}>
                                                {sub.sub_cat_name}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    {errors.sub_category_id && (
                                        <Typography sx={{color: "red", fontSize: "14px"}}>
                                            {errors.sub_category_id}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography sx={{fontSize: "18px", fontWeight: 500}}>
                                    Challenge Icon (optional)
                                </Typography>
                                <TextField
                                    type="file"
                                    inputProps={{accept: "image/*"}}
                                    onChange={handleFileChange}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>XP reward</Typography>
                                <TextField
                                    name="xp_points"
                                    type="number"
                                    placeholder="Enter xp points"
                                    fullWidth
                                    value={challengeData.xp_points}
                                    onChange={handleChange}
                                />
                                {errors.xp_points && (
                                    <Typography sx={{color: "red", fontSize: "14px"}}>
                                        {errors.xp_points}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Status</Typography>
                                    <Select
                                        name="is_active"
                                        value={challengeData.is_active}
                                        onChange={handleChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            Select Status
                                        </MenuItem>
                                        <MenuItem value="1">Active</MenuItem>
                                        <MenuItem value="0">In-Active</MenuItem>
                                    </Select>

                                    {errors.is_active && (
                                        <Typography sx={{color: "red", fontSize: "14px"}}>
                                            {errors.is_active}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Who can earn</Typography>
                                    <Select
                                        name="user_type"
                                        value={challengeData.user_type}
                                        onChange={handleChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            Select User Type
                                        </MenuItem>
                                        {challengeData.criteria_type !== 7 && (
                                            <MenuItem value="user">User</MenuItem>
                                        )}
                                        <MenuItem value="trainer">Trainer</MenuItem>
                                    </Select>

                                    {errors.user_type && (
                                        <Typography sx={{color: "red", fontSize: "14px"}}>
                                            {errors.user_type}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Reward Badge</Typography>
                                    <Select
                                        name="reward_badge_id"
                                        value={challengeData.reward_badge_id}
                                        onChange={handleChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            Select Badge
                                        </MenuItem>
                                        {badgesOptions.map((option) => (
                                            <MenuItem key={option.id} value={option.id}>
                                                {option.badge_name} ({option.badge_code})
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    {errors.reward_badge_id && (
                                        <Typography sx={{ color: "red", fontSize: "14px" }}>
                                            {errors.reward_badge_id}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item md={12}>
                                <Typography sx={{mb: 1, fontSize: "18px", fontWeight: 500}}>
                                    Description
                                </Typography>
                                <textarea name="description" placeholder="Description" cols="5" rows="5"
                                          style={{
                                              width: "100%",
                                              minHeight: "150px",
                                              paddingTop: "15px",
                                              borderRadius: "10px",
                                              border: "1px solid #c4c4c4",
                                              paddingLeft: "15px",
                                              fontSize: "16px",
                                              outlineColor: "none"
                                          }}
                                          value={challengeData.description}
                                          onChange={handleChange}
                                ></textarea>

                                {errors.description && (
                                    <Typography sx={{color: "red", fontSize: "14px"}}>
                                        {errors.description}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{display: "flex", justifyContent: "flex-end", mt: 2}}>
                                    <Button
                                        variant="contained"
                                        disabled={isSubmitting}
                                        sx={{
                                            background: "linear-gradient(181.45deg, #7AACFF -80.86%, rgba(60, 117, 213, 0.98) 98.77%)",
                                            color: "#fff",
                                            textTransform: "none",
                                            "&:hover": {
                                                background: "linear-gradient(181.45deg, #7AACFF -80.86%, rgba(60, 117, 213, 0.98) 98.77%)",
                                            },
                                        }}
                                        type="submit"
                                    >
                                        Create Challenge
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
}

export default ConvertChallengeScreen;